// [PURPOSE]: Canonical list of built-in shop types, grouped by category.
// [WHY]: ShopTab.js had a hardcoded 70-line `<optgroup>` block for the
//   Generate-form's shop-type dropdown (line ~614-685). EditShopDialog
//   needs the SAME list so the user can swap a shop's bound type after
//   creation. Two copies would drift; per Rule 22 we extract once and
//   import twice.
// [INTEGRATION]:
//   - ShopTab.js — Generate-form shop-type select.
//   - EditShopDialog.jsx — Edit-form shop-type select.
//   - Both also append the user's `customShopTypes` array beneath the
//     built-in groups.

/**
 * Built-in shop type groups. Each group has a `label` (rendered as
 * <optgroup label>) and an `options` array of `{value, label}` pairs
 * (rendered as <option>).
 *
 * `value` MUST match a key in App.jsx's SHOP_ROLL_TABLES const,
 * otherwise the generator's lookup at `SHOP_ROLL_TABLES[shopType]`
 * returns undefined and the shop fails with "Shop type not found".
 */
export const SHOP_TYPE_GROUPS = [
  {
    label: '📜 Official D&D 5e Tables (DMG)',
    options: [
      { value: 'general_dmg', label: '🏪 General Store (DMG)' },
      { value: 'weapon_shop_dmg', label: '⚔️ Weaponsmith (DMG)' },
      { value: 'armor_shop_dmg', label: '🛡️ Armorer (DMG)' },
      { value: 'individual_treasure', label: '💰 Individual Treasure (DMG)' },
    ],
  },
  {
    label: '🏪 Custom Shops (Homebrew)',
    options: [
      { value: 'general', label: '🏪 General Store (Custom)' },
      { value: 'weapon_shop', label: '⚔️ Weapon Shop (Custom)' },
      { value: 'armor_shop', label: '🛡️ Armor Shop (Custom)' },
      { value: 'blacksmith', label: '🔨 Blacksmith (Custom)' },
      { value: 'apothecary', label: '🧪 Apothecary (Custom)' },
      { value: 'tavern', label: '🍺 Tavern (Custom)' },
      { value: 'temple', label: '⛪ Temple (Custom)' },
      { value: 'jeweler', label: '💎 Jeweler (Custom)' },
      { value: 'library', label: '📚 Library/Scribe (Custom)' },
    ],
  },
  {
    label: '🔮 Custom Spellcasters (Homebrew)',
    options: [
      { value: 'magic_shop', label: '🔮 Magic Shop (Custom)' },
      { value: 'court_wizard', label: '👑 Court Wizard (Custom)' },
      { value: 'hut_witch', label: '🏚️ Hut Witch (Custom)' },
      { value: 'hermit_sage', label: '🧙 Hermit Sage (Custom)' },
      { value: 'war_mage', label: '⚡ War Mage (Custom)' },
    ],
  },
  {
    label: '🌟 Custom Exotic Traders (Homebrew)',
    options: [
      { value: 'planar_merchant', label: '🌀 Planar Merchant (Custom)' },
      { value: 'dragon_hoard_trader', label: '🐉 Dragon Hoard Trader (Custom)' },
      { value: 'underdark_trader', label: '🕳️ Underdark Trader (Custom)' },
      { value: 'elemental_trader', label: '🔥 Elemental Trader (Custom)' },
      { value: 'sea_trader', label: '🌊 Sea Trader (Custom)' },
      { value: 'caravan_trader', label: '🐪 Caravan Trader (Custom)' },
    ],
  },
  {
    label: '🛠️ Custom Specialized Artisans (Homebrew)',
    options: [
      { value: 'artificer_workshop', label: '⚙️ Artificer Workshop (Custom)' },
      { value: 'beast_tamer', label: '🐺 Beast Tamer (Custom)' },
      { value: 'clockwork_tinker', label: '⏰ Clockwork Tinker (Custom)' },
      { value: 'wandmaker', label: '🪄 Wandmaker (Custom)' },
      { value: 'enchanter', label: '✨ Enchanter (Custom)' },
      { value: 'gemcutter', label: '💎 Gemcutter (Custom)' },
    ],
  },
  {
    label: '🗡️ Custom Underground (Homebrew)',
    options: [
      { value: 'fence', label: '🗡️ Fence (Custom)' },
      { value: 'smuggler_den', label: "📦 Smuggler's Den (Custom)" },
      { value: 'assassins_guild', label: "🗡️ Assassin's Guild (Custom)" },
      { value: 'black_market', label: '🕳️ Black Market (Custom)' },
      { value: 'thieves_quartermaster', label: "🗝️ Thieves' Quartermaster (Custom)" },
    ],
  },
  {
    label: '🙏 Custom Religious (Homebrew)',
    options: [
      { value: 'shrine_keeper', label: '🕯️ Shrine Keeper (Custom)' },
      { value: 'cult_supplier', label: '🔺 Cult Supplier (Custom)' },
      { value: 'divine_armorer', label: '⚡ Divine Armorer (Custom)' },
      { value: 'pilgrim_outfitter', label: '🎒 Pilgrim Outfitter (Custom)' },
    ],
  },
  {
    label: '🎪 Custom Entertainment & Travel (Homebrew)',
    options: [
      { value: 'carnival_vendor', label: '🎠 Carnival Vendor (Custom)' },
      { value: 'tavern_keeper', label: '🍺 Tavern Keeper (Custom)' },
      { value: 'caravan_master', label: '🐪 Caravan Master (Custom)' },
      { value: 'ship_chandler', label: '⛵ Ship Chandler (Custom)' },
      { value: 'roadside_merchant', label: '🛤️ Roadside Merchant (Custom)' },
    ],
  },
];

/**
 * Find the human-readable label for a shop type id. Searches the built-in
 * groups first, then the user's customShopTypes array. Returns null when
 * no match (caller can fall back to a `replace(/_/g, ' ')` titlecase).
 *
 * @param {string} shopTypeId
 * @param {Array} customShopTypes - App-state customShopTypes array
 * @returns {string|null}
 */
export function findShopTypeLabel(shopTypeId, customShopTypes = []) {
  if (!shopTypeId) return null;
  for (const group of SHOP_TYPE_GROUPS) {
    const match = group.options.find((o) => o.value === shopTypeId);
    if (match) return match.label;
  }
  const custom = (customShopTypes || []).find((s) => s.id === shopTypeId);
  if (custom) return `${custom.icon || '🏪'} ${custom.name}`;
  return null;
}
